import { z } from 'zod'
import { DATE_INPUT_DESCRIPTION, DateInputSchema, DateRangeFilterSchema, UTCMillisecondsSchema } from '../time'
import { TagIdSchema } from '../tag'
import { SmallUserSchema } from '../smallUser'
import { RoleIdSchema } from '../role'
import { TinyResidentSchema } from '../resident'
import { PinPlacementSchema } from '../locations/model'
import { LatLngSchema } from '../locations/latLng'
import { LanguageCodeSchema } from '../languageCode'
import { WithImportIdSchema } from '../import'
import {
  AccountIdSchema,
  AccountStatusIdSchema,
  CompanyIdSchema,
  idSchemaV2,
  isId,
  LocationIdSchema,
  TeamIdSchema,
  UserIdSchema,
  WithAccountIdSchema,
  WorkflowActionIdSchema,
  WorkflowIdSchema,
  WorkflowStageIdSchema,
  type AccountId,
  type TeamId,
  type UserId,
} from '../ids'
import { PaginatedCursorInputSchema, PaginatedOutputSchema } from '../elasticApi'
import { DispositionIdSchema } from '../disposition'
import {
  CustomFieldFilterSchema,
  CustomFieldMapSchema,
  CustomFieldTypeSchema,
  RangeFilterSchema,
  WithCustomFieldsSchema,
  type WithCustomFields,
} from '../customFieldMap'
import { CustomFieldIdSchema } from '../customField'
import { ContactIdSchema, ContactMethodSchema } from '../contact/model'
import { AuditPropsSchema } from '../common'
import { SmallAddressSchema, type SmallAddress } from '../address'

export type AccountOwnerId = UserId | TeamId

export const AccountUserDetailsSchema = SmallUserSchema.omit({ userId: true, memberOf: true })
export type AccountUserDetails = z.infer<typeof AccountUserDetailsSchema>

export function toAccountAddress<T extends SmallAddress>(item?: T): SmallAddress | undefined {
  if (!item) return
  return {
    line1: item.line1,
    locality: item.locality,
    countrySubd: item.countrySubd,
    postal1: item.postal1,
    unitNbr: item.unitNbr,
    latlng: item.latlng,
  }
}

export type StatusHistoryItem = z.infer<typeof StatusHistoryItemSchema>

export type AccountImport = WithCustomFields & {
  raw: string
  line1?: string
  unitNbr?: string
  unitType?: string
  locality?: string
  countrySubd?: string
  postal1?: string
  accountId?: AccountId
  latitude?: number
  longitude?: number
  status?: string
  source?: string
  sourceId?: string
  sourceStatus?: string
  closedDate?: string
  approvedDate?: string
  installedDate?: string
  owner?: UserId
  closer?: UserId
  team?: string
  firstName?: string
  lastName?: string
  email?: string
  phone?: string
  notes?: string
  expiresAt?: number
  externalLeadId?: string
  statusHistory?: StatusHistoryItem[]
}

export const StatusHistoryItemSchema = z.object({
  statusId: AccountStatusIdSchema,
  sourceStatus: z.string().optional(),
  statusChangedDate: UTCMillisecondsSchema,
})

export const WorkflowHistorySourceSchema = z.enum(['rollback'])
export type WorkflowHistorySource = z.infer<typeof WorkflowHistorySourceSchema>

export const WorkflowHistoryItemSchema = z.object({
  stageId: WorkflowStageIdSchema,
  actionId: WorkflowActionIdSchema.optional(),
  userId: UserIdSchema,
  timestamp: UTCMillisecondsSchema,
  latlng: LatLngSchema.optional().describe('Location where the action was taken'),
  sourceStatus: z.string().optional().describe('External status from source system at the time of action'),
  source: WorkflowHistorySourceSchema.optional().describe(
    'Set when the entry was written by a bulk process (e.g. rollback) that must not earn new stage credit'
  ),
})
export type WorkflowHistoryItem = z.infer<typeof WorkflowHistoryItemSchema>

const TimestampSchema = z.object({
  timestamp: UTCMillisecondsSchema,
})

export const UserHistorySchema = z.array(
  z.object({
    userId: UserIdSchema,
    ...TimestampSchema.shape,
  })
)
export type UserHistory = z.infer<typeof UserHistorySchema>

export const TeamHistorySchema = z.array(
  z.object({
    teamId: TeamIdSchema,
    ...TimestampSchema.shape,
  })
)
export type TeamHistory = z.infer<typeof TeamHistorySchema>

export const UnsavedAccountContactSchema = z.object({
  ...TimestampSchema.shape,
  method: ContactMethodSchema,
  userId: UserIdSchema,
  contactId: ContactIdSchema.optional(),
  dispositionId: DispositionIdSchema,
  deviceLocation: LatLngSchema.optional(),
  isPitch: z.boolean().optional(),
})
export type UnsavedAccountContact = z.infer<typeof UnsavedAccountContactSchema>
export const AccountContactSchema = z.object({
  ...UnsavedAccountContactSchema.shape,
  contactId: ContactIdSchema,
})

export type AccountContact = z.infer<typeof AccountContactSchema>

export const NoteIdSchema = idSchemaV2('Note')
export type NoteId = z.infer<typeof NoteIdSchema>
export const isNoteId = isId(NoteIdSchema)

export const UnsavedAccountNoteSchema = z.object({
  ...TimestampSchema.shape,
  text: z.string(),
  userId: UserIdSchema,
  noteId: NoteIdSchema.optional(),
})
export type UnsavedAccountNote = z.infer<typeof UnsavedAccountNoteSchema>
export const AccountNoteSchema = z.object({
  ...UnsavedAccountNoteSchema.shape,
  noteId: NoteIdSchema,
})

export type AccountNote = z.infer<typeof AccountNoteSchema>

const TagWithDateSchema = z.object({
  tagId: TagIdSchema,
  timestamp: UTCMillisecondsSchema,
  userId: UserIdSchema,
})
export type TagWithDate = z.infer<typeof TagWithDateSchema>

export const UnsavedAccountSchema = z.object({
  ...WithCustomFieldsSchema.shape,
  ...WithImportIdSchema.shape,
  workflowId: WorkflowIdSchema.optional().meta({ description: 'Workflow ID that is associated with the account' }),
  workflowHistory: z.array(WorkflowHistoryItemSchema).optional().meta({
    description: 'List of workflow stages and actions that the account has progressed through',
  }),
  workflowStageId: WorkflowStageIdSchema.optional(),
  workflowActionId: WorkflowActionIdSchema.optional(),
  deviceLocation: LatLngSchema.optional(),
  checkpoints: WorkflowStageIdSchema.array().optional(),

  accountId: AccountIdSchema.optional(),
  statusId: AccountStatusIdSchema.optional().meta({ example: 'AS.test', deprecated: true }),
  accountSource: z.string().optional().meta({
    description:
      'Optional label for the system this account came from. Stored on the account. Not required to match or look up an account by sourceId.',
    example: 'Statra',
  }),
  sourceStatus: z.string().optional().meta({ description: 'Raw status from the source system' }),
  sourceId: z.string().optional().meta({
    description:
      'External identifier for the account within your company. sourceId is a single-part key: matching and lookup use sourceId alone. accountSource is not part of the key and is not required.',
  }),
  externalLeadId: z.string().optional().meta({ description: 'Alternate external lead identifier' }),
  locationId: LocationIdSchema.optional(),
  location: SmallAddressSchema.optional(),

  ownerId: UserIdSchema.optional().meta({ description: 'User ID of the account owner', example: 'U.test' }),
  owner: AccountUserDetailsSchema.optional().meta({ description: 'Details of the account owner' }),

  closerId: UserIdSchema.optional().meta({ description: 'User ID of the account closer' }),
  closer: AccountUserDetailsSchema.optional().meta({ description: 'Details of the account closer' }),

  resident: TinyResidentSchema.optional().meta({ description: 'Primary resident information' }),
  resident2: TinyResidentSchema.optional().meta({ description: 'Secondary resident information' }),
  language: LanguageCodeSchema.optional().meta({
    description: 'Primary language spoken for the customer or household',
  }),

  /** @deprecated */
  rescheduleCount: z
    .number()
    .optional()
    .meta({ description: 'Number of times appointment was rescheduled (deprecated)', deprecated: true }),

  /** @deprecated */
  dealSize: z.number().optional().meta({ description: 'Estimated deal size (deprecated)', deprecated: true }),

  appointmentDate: UTCMillisecondsSchema.optional().meta({ description: 'Appointment date as UTC milliseconds' }),
  /** this is a test record and should be ignored by downstream systems */
  test: z.boolean().optional().meta({ description: 'Flag indicating if this is a test record' }),
  teamId: TeamIdSchema.optional().meta({ description: 'ID of team this account belongs to' }),
  contacts: UnsavedAccountContactSchema.array().optional().meta({ description: 'List of account contacts' }),
  notes: UnsavedAccountNoteSchema.array().optional().meta({ description: 'List of account notes' }),

  /** Only available for multi accounts
   * On an untouched property, an account is created with isParent = true
   */
  isParent: z.boolean().optional().meta({ description: 'Flag indicating if this is a parent account' }),
  /** Only available for multi accounts
   * Any other accounts added to a property with an account that isParent = true will need to set the parentAccountId
   * resident business name will default to the parent account's business name
   */
  parentAccountId: AccountIdSchema.optional().meta({ description: 'Parent account identifier' }),
  tags: TagWithDateSchema.array().optional().meta({ description: 'List of tags associated with the account' }),
})

export type UnsavedAccount = z.infer<typeof UnsavedAccountSchema>

export const TinyWorkflowAccountSchema = z.object({
  accountId: AccountIdSchema,
  workflowId: WorkflowIdSchema,
  workflowStageId: WorkflowStageIdSchema,
  workflowActionId: WorkflowActionIdSchema.optional(),
  workflowLastActionDate: UTCMillisecondsSchema.optional(),
  workflowHistory: WorkflowHistoryItemSchema.array(),
  customFields: CustomFieldMapSchema.optional(),
})
export type TinyWorkflowAccount = z.infer<typeof TinyWorkflowAccountSchema>

export const AccountDataSchema = z.object({
  ...AuditPropsSchema.shape,
  ...UnsavedAccountSchema.omit({
    contacts: true,
    notes: true,
  }).shape,
  ...WithAccountIdSchema.shape,
  statusHistory: z.array(StatusHistoryItemSchema).meta({ deprecated: true }),
  workflowStageId: WorkflowStageIdSchema.optional().meta({
    description: 'Current workflow stage ID that the account is in',
  }),
  workflowActionId: WorkflowActionIdSchema.optional().meta({
    description: 'Current workflow action ID that the account is in',
  }),
  workflowStageName: z.string().optional().meta({
    description: 'Name of the current workflow stage, resolved from the workflow definition',
  }),
  workflowActionName: z.string().optional().meta({
    description: 'Name of the current workflow action, resolved from the workflow definition',
  }),
  homeowner: TinyResidentSchema.optional().meta({
    description: 'Homeowner resident information, included for convenience but may be incomplete',
  }),
  expiresAt: UTCMillisecondsSchema.optional(),
  contacts: z.array(AccountContactSchema).optional(),
  notes: z.array(AccountNoteSchema).optional(),
  lastActionDate: UTCMillisecondsSchema.optional(),
  recruitId: UserIdSchema.optional().meta({ description: 'UserId of the recruit who was assigned to the account' }),
  ownerHistory: UserHistorySchema.optional().meta({ description: 'History of account owners' }),
  closerHistory: UserHistorySchema.optional().meta({ description: 'History of account closers' }),
  teamHistory: TeamHistorySchema.optional().meta({ description: 'History of account teams' }),
  assignmentDate: UTCMillisecondsSchema.optional().meta({
    description:
      'When the latest area assignment run that touched this account started, in UTC epoch milliseconds; matches that run in the area run history',
  }),
  editable: z.boolean().optional().meta({
    description: 'Whether the current user can edit this account. Decorated per-request, not stored.',
  }),
  pinPlacement: PinPlacementSchema.optional().meta({
    description:
      'How the account pin was placed during import. Reads return unknown values for pins created before placement tracking.',
  }),
})
export type AccountData = z.infer<typeof AccountDataSchema>

export const AccountSortBySchema = z
  .union([z.enum(['createdDate', 'lastActionDate', 'lastUpdatedDate']), CustomFieldIdSchema])
  .meta({
    description: 'Field to sort accounts by.',
  })
export type AccountSortBy = z.infer<typeof AccountSortBySchema>

export const AccountSortOrderSchema = z.enum(['asc', 'desc']).meta({
  description: 'Sort direction.',
})
export type AccountSortOrder = z.infer<typeof AccountSortOrderSchema>

export const AccountStatusSearchInputSchema = z.object({
  accountIds: z.array(AccountIdSchema).optional().meta({ description: 'List of account IDs to filter by.' }),
  workflowId: WorkflowIdSchema.optional().meta({ description: 'ID of the workflow to filter by.' }),
  stageIds: z.array(WorkflowStageIdSchema).optional().meta({ description: 'List of workflow stage IDs to filter by.' }),
  actionIds: z
    .array(WorkflowActionIdSchema)
    .optional()
    .meta({ description: 'List of workflow action IDs to filter by.' }),
  statusIds: z
    .array(AccountStatusIdSchema)
    .meta({ description: 'List of account status IDs to filter by. Deprecated if using Workflows.', deprecated: true })
    .default([]),
  teamId: TeamIdSchema.optional().meta({ description: 'ID of the team to filter by.' }),
  teamIds: TeamIdSchema.array().optional().meta({ description: 'List of team IDs to filter by.' }),
  userId: UserIdSchema.optional().meta({ description: 'ID of the user to filter by.' }),
  roleIds: RoleIdSchema.array().optional().meta({ description: 'List of role IDs to filter by.' }),
  startTime: DateInputSchema.optional().meta({
    description: `Start of the search's date range, inclusive. ${DATE_INPUT_DESCRIPTION}`,
  }),
  endTime: DateInputSchema.optional().meta({
    description: `End of the search's date range, inclusive. ${DATE_INPUT_DESCRIPTION}`,
  }),
  ...PaginatedCursorInputSchema.shape,
  query: z.string().optional(),
  searchByCurrentStage: z.boolean().optional().meta({
    description: 'Whether to search by the most recent stage in the status history. The default value is false',
  }),
  tagIds: z.array(TagIdSchema).optional().meta({ description: 'Filter by tag IDS' }),
  cities: z.array(z.string()).optional().meta({ description: 'Filter by city names.' }),
  states: z.array(z.string()).optional().meta({ description: 'Filter by state names.' }),
  zipCodes: z.array(z.string()).optional().meta({ description: 'Filter by zip codes.' }),
  customFields: CustomFieldFilterSchema.optional().meta({
    description: 'Filter by custom field values (range, multipleChoice, or exists).',
  }),
  coordinates: z.array(LatLngSchema).min(3).optional().meta({ description: 'Geo polygon coordinates to filter by.' }),
  contactCount: RangeFilterSchema.optional().meta({ description: 'Range filter on contact count.' }),
  workflowActionCount: RangeFilterSchema.optional().meta({ description: 'Range filter on workflow action count.' }),
  lastActionDate: DateRangeFilterSchema.optional().meta({ description: 'Range filter on last action date.' }),
  stageEntryDate: DateRangeFilterSchema.optional().meta({
    description: 'Range filter on current workflow stage entry date.',
  }),
  sortBy: AccountSortBySchema.optional().meta({ description: 'Field to sort accounts by. Defaults to createdDate.' }),
  sortOrder: AccountSortOrderSchema.optional().meta({ description: 'Sort direction. Defaults to asc.' }),
})
export type AccountStatusSearchInput = z.infer<typeof AccountStatusSearchInputSchema>
export const AccountDataResponseSchema = z.object({
  accounts: z.array(AccountDataSchema),
  ...PaginatedOutputSchema.shape,
})
export type AccountDataResponse = z.infer<typeof AccountDataResponseSchema>

export type AccountStatusSearchFilter = Partial<AccountStatusSearchInput>

export const BulkUpdateStatusActionSchema = z.object({
  actionType: z.literal('updateStatus'),
  statusId: AccountStatusIdSchema.meta({ description: 'Status ID to set on matched accounts.' }),
})
export type BulkUpdateStatusAction = z.infer<typeof BulkUpdateStatusActionSchema>

export const BulkUpdateStageActionSchema = z.object({
  actionType: z.literal('updateStage'),
  stageId: WorkflowStageIdSchema.meta({ description: 'Workflow stage ID to move matched accounts to.' }),
})
export type BulkUpdateStageAction = z.infer<typeof BulkUpdateStageActionSchema>

export const BulkUpdateCustomFieldActionSchema = z.object({
  actionType: z.literal('updateCustomField'),
  fieldId: CustomFieldIdSchema.meta({ description: 'Custom field ID to update on matched accounts.' }),
  value: CustomFieldTypeSchema.meta({
    description: 'Value to set. Use null to clear the field.',
  }),
})
export type BulkUpdateCustomFieldAction = z.infer<typeof BulkUpdateCustomFieldActionSchema>

export const BulkDeleteAccountOrContactActionSchema = z.object({
  actionType: z
    .union([z.literal('delete'), z.literal('clearDisposition')])
    .meta({ description: '`delete` removes matched accounts. `clearDisposition` clears disposition contacts.' }),
})

export const BulkModifyAccountsActionSchema = z
  .array(
    z.discriminatedUnion('actionType', [
      BulkUpdateStatusActionSchema,
      BulkUpdateStageActionSchema,
      BulkUpdateCustomFieldActionSchema,
      BulkDeleteAccountOrContactActionSchema,
    ])
  )
  .meta({ description: 'Bulk actions to apply to each matched account.' })
export type BulkModifyAccountsAction = z.infer<typeof BulkModifyAccountsActionSchema>

export const BulkRollbackStageActionSchema = z.object({
  actionType: z.literal('rollbackStage'),
  workflowId: WorkflowIdSchema,
})
export type BulkRollbackStageAction = z.infer<typeof BulkRollbackStageActionSchema>

// Not part of the public bulk input: only the rollback endpoints can queue a rollback
const AccountUpdateQueueActionsSchema = z.array(
  z.discriminatedUnion('actionType', [
    BulkUpdateStatusActionSchema,
    BulkUpdateStageActionSchema,
    BulkUpdateCustomFieldActionSchema,
    BulkDeleteAccountOrContactActionSchema,
    BulkRollbackStageActionSchema,
  ])
)
export type AccountUpdateQueueActions = z.infer<typeof AccountUpdateQueueActionsSchema>

const BulkModifyAccountsSchema = z.object({
  accountId: AccountIdSchema,
  actions: AccountUpdateQueueActionsSchema,
  companyId: CompanyIdSchema,
  userId: UserIdSchema,
})
export type BulkModifyAccounts = z.infer<typeof BulkModifyAccountsSchema>
